// @givmo/design-system — Toaster / snackbar (sonner, themed to tokens).
//
// 03-3c feedback tier. Ports the Flutter `showCustomSnackBar` helper (givmo_UI_components
// helper/show_snack_bar.dart): a floating bar with a black radius-all15 surface, soft shadow
// (Flutter elevation 6), 24/14 padding, a leading icon, and gray20 text. The Flutter helper
// clears prior snackbars before showing a new one; sonner stacks/dedupes by id instead.
//
// We render sonner fully `unstyled` and theme it through token utility classes (so NO sonner
// default CSS and NO raw color values leak in). Apps mount one <Toaster/> at the root, then
// call the typed `toast` helper (re-exported below) — `toast(msg)`, `toast.success(msg)`,
// `toast.error(msg)`, `toast.dismiss()`.
//
// a11y — the politeness split. The house map across the estate is `role="alert"` for errors and
// polite/`role="status"` for everything else. sonner hardcodes `aria-live="polite"` on the
// <section> it renders and exposes NO prop to change it (2.0.7 `ToasterProps` carries only
// `containerAriaLabel`), so one <Toaster> cannot express two politeness levels: an error would
// queue behind whatever is already speaking instead of interrupting.
//
// So we mount TWO sonner toasters and partition the toasts between them with sonner's own
// multi-toaster routing (`toasterId`): a <Toaster id> renders only toasts carrying that id, and a
// <Toaster> with no id renders only toasts carrying none. Errors go to the assertive one, every
// other severity stays polite. The partition is mutually exclusive, so each toast renders — and is
// announced — exactly once.
//
// Be honest about the size of this: SC 4.1.3 was already satisfied (the message WAS announced).
// This is a politeness/correctness fix, not a floor breach.
import { useCallback } from 'react';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import type { ToasterProps as SonnerToasterProps } from 'sonner';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

export interface ToasterProps extends TestIdProps {
  /** Screen corner for the stack (default 'bottom-center' — Flutter floating behavior). */
  position?: SonnerToasterProps['position'];
  /** Auto-dismiss duration in ms (default 4000). */
  duration?: number;
  className?: string;
}

/**
 * Toasts tagged with this `toasterId` render in the assertive region instead of the polite one.
 * Internal: callers never pass it — `toast.error` injects it (see the wrapped `toast` below).
 */
const ASSERTIVE_TOASTER_ID = 'givmo-assertive';

// Kit snackbar surface: black all15 bar, soft shadow, 24/14 padding, gray20 Lato text + icon.
const TOAST_SURFACE = cn(
  'flex w-full items-center gap-8 rounded-all15 bg-black px-24 py-14 shadow-soft',
  'font-sans text-s16 font-w600 tracking-givmo text-gray20',
);

// A toast's act ("Undo"): a bordered button on the black bar, so it reads as one (tester-5's item 5 on the
// season setup's remove toast). sonner's own button look is scoped to its styled toasts, which these
// never are, so without it the act would be bare text.
const TOAST_ACTION = cn(
  'ml-8 inline-flex min-h-[36px] shrink-0 items-center rounded-all12 border border-gray60 px-12',
  'font-sans text-s14 font-w700 tracking-givmo text-white outline-none transition-colors',
  'hover:bg-gray80 focus-visible:shadow-focus-glow max-lg:min-h-44',
);

// Token-themed icons (gray20 to match the label, like the Flutter CIcon gray20). Both regions get
// the same set so an error toast is visually identical wherever it is routed.
const TOAST_ICONS: SonnerToasterProps['icons'] = {
  success: <Icon icon={CircleCheck} size={24} className="text-gray20" aria-hidden />,
  error: <Icon icon={CircleAlert} size={24} className="text-gray20" aria-hidden />,
  info: <Icon icon={Info} size={24} className="text-gray20" aria-hidden />,
  warning: <Icon icon={TriangleAlert} size={24} className="text-gray20" aria-hidden />,
};

/** Single <Toaster/> mounted at the app root. Themed to the Givmo snackbar tokens. */
export function Toaster({
  position = 'bottom-center',
  duration = 4000,
  className,
  testId,
}: ToasterProps) {
  // sonner writes `aria-live="polite"` straight into `React.createElement('section', …)` with no
  // prop in front of it, so the only way to promote this region is the ref it already forwards to
  // that section. The callback runs at commit, after React has written the element's own
  // attributes, and again if the section ever remounts. It does not need re-applying on every
  // render: React rewrites an attribute only when the JSX value CHANGES, and sonner's is a
  // constant. `role="alert"` is the house signal for errors; sonner's `aria-atomic="false"`
  // survives it, which is what we want — each new error announces on its own instead of re-reading
  // the whole stack.
  const promoteToAssertive = useCallback((node: HTMLElement | null) => {
    node?.setAttribute('aria-live', 'assertive');
    node?.setAttribute('role', 'alert');
  }, []);

  const shared = {
    position,
    duration,
    icons: TOAST_ICONS,
    toastOptions: {
      unstyled: true,
      classNames: {
        toast: cn(TOAST_SURFACE, className),
        title: 'flex-1',
        icon: 'flex shrink-0 items-center',
        description: 'text-gray40',
        actionButton: TOAST_ACTION,
      },
    },
  } satisfies Partial<SonnerToasterProps>;

  return (
    <div {...testIdProp(testId)}>
      {/* Polite region — every severity EXCEPT error. */}
      <SonnerToaster {...shared} containerAriaLabel="Notifications" />
      {/*
        Assertive region — errors only. Same position, so an error appears exactly where a toast
        always does. The two stacks are independently positioned `fixed` elements, so while an
        error and a non-error are on screen at the same time they occupy the same coordinates; the
        inline z-index puts the error on top, which is the right precedence for the more urgent
        message.
      */}
      <SonnerToaster
        {...shared}
        id={ASSERTIVE_TOASTER_ID}
        ref={promoteToAssertive}
        containerAriaLabel="Errors"
        style={{ zIndex: 1000000000 }}
      />
    </div>
  );
}

// sonner's `toast.error` renders into whichever toaster matches its `toasterId`; tagging it here
// means the existing `toast.error(…)` call sites across the apps (153 at the time of writing)
// become assertive without touching one of them. An explicit `toasterId` from the caller wins.
const assertiveError: typeof sonnerToast.error = (message, data) =>
  sonnerToast.error(message, { ...data, toasterId: data?.toasterId ?? ASSERTIVE_TOASTER_ID });

/**
 * sonner's typed `toast` API, with `error` routed to the assertive region.
 *
 * `toast(msg)` is the showCustomSnackBar default; `toast.success` / `toast.error` / `toast.info` /
 * `toast.warning` / `toast.dismiss` are the themed variants (each renders the kit surface with its
 * matching icon).
 *
 * Known limit: `toast.promise` is NOT split. One promise toast is a single toast that transitions
 * loading → success/error, so routing it assertively would make its loading and success states
 * interrupt too. It has no call sites in this repo today; a promise toast whose error state must
 * interrupt should call `toast.error` in its own `catch`.
 */
export const toast: typeof sonnerToast = Object.assign(
  ((message, data) => sonnerToast(message, data)) as typeof sonnerToast,
  sonnerToast,
  { error: assertiveError },
);
